import React, { useState, useEffect } from 'react';
import { Card, Form, Select, Switch, InputNumber, Col, Row, Typography } from 'antd';

const GRID_SPANS = [
    { label: '1/4 width (6)', value: 6 },
    { label: '1/3 width (8)', value: 8 },
    { label: '1/2 width (12)', value: 12 },
    { label: '2/3 width (16)', value: 16 },
    { label: '3/4 width (18)', value: 18 },
    { label: 'Full width (24)', value: 24 },
];

export interface HeatmapConfig {
    includeChildHeatmap: boolean;
    excludeParentHeatmap: boolean;
    childHeatmapDepth: number | null;
    heatmapSpan: number | null;
    childHeatmapSpan: number | null;
    heatmapGridResolutionX: number | null;
    heatmapGridResolutionY: number | null;
    hexagonSizeMeters: number | null;
    templateOpacity: number | null;
    heatmapOpacity: number | null;
    heatmapColorScheme: string | null;
    invertColors: boolean | null;
    heatmapValueRangeMode: 'auto' | 'fixed' | 'areaDefault' | null;
    heatmapValueMin: number | null;
    heatmapValueMax: number | null;
}

const DEFAULT_HEATMAP_CONFIG: HeatmapConfig = {
    includeChildHeatmap: false,
    excludeParentHeatmap: false,
    childHeatmapDepth: null,
    heatmapSpan: 24,
    childHeatmapSpan: 24,
    heatmapGridResolutionX: 50,
    heatmapGridResolutionY: null,
    hexagonSizeMeters: 50,
    templateOpacity: 70,
    heatmapOpacity: 90,
    heatmapColorScheme: 'blue-red',
    invertColors: false,
    heatmapValueRangeMode: 'auto',
    heatmapValueMin: null,
    heatmapValueMax: null,
};

interface Props {
    initialConfig?: Partial<HeatmapConfig>;
    onChange: (config: HeatmapConfig) => void;
}

export const SectionConfigHeatmap: React.FC<Props> = ({ initialConfig, onChange }) => {
    const [config, setConfig] = useState<HeatmapConfig>({
        ...DEFAULT_HEATMAP_CONFIG,
        ...initialConfig,
    });

    const updateField = (field: keyof HeatmapConfig, value: any) => {
        const newConfig = { ...config, [field]: value };
        setConfig(newConfig);
        onChange(newConfig);  // Call onChange directly to avoid infinite loop
    };

    return (
        <Card size="small" style={{ background: '#f5f5f5' }}>
            <Typography.Text strong style={{ display: 'block', marginTop: 16, marginBottom: 12 }}>
                Layout Settings
            </Typography.Text>
            <Row gutter={12}>
                <Col span={8}>
                    <Form.Item label="Include child heatmaps">
                        <Switch
                            checked={config.includeChildHeatmap}
                            onChange={(checked) => updateField('includeChildHeatmap', checked)}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Exclude parent heatmap">
                        <Switch
                            checked={config.excludeParentHeatmap}
                            onChange={(checked) => updateField('excludeParentHeatmap', checked)}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Child depth">
                        <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            value={config.childHeatmapDepth ?? undefined}
                            onChange={(val) => updateField('childHeatmapDepth', val)}
                            placeholder="Auto"
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Parent width (grid span)">
                        <Select
                            allowClear
                            options={GRID_SPANS}
                            value={config.heatmapSpan ?? undefined}
                            onChange={(val) => updateField('heatmapSpan', val)}
                            placeholder="Full width"
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Child width (grid span)">
                        <Select
                            allowClear
                            options={GRID_SPANS}
                            value={config.childHeatmapSpan ?? undefined}
                            onChange={(val) => updateField('childHeatmapSpan', val)}
                            placeholder="Full width"
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Typography.Text strong style={{ display: 'block', marginTop: 16, marginBottom: 12 }}>
                Grid Resolution (Template Mode)
            </Typography.Text>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item label="Grid resolution X">
                        <InputNumber
                            min={4}
                            max={200}
                            style={{ width: '100%' }}
                            value={config.heatmapGridResolutionX ?? 50}
                            onChange={(val) => updateField('heatmapGridResolutionX', val)}
                            placeholder="50"
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Grid resolution Y">
                        <InputNumber
                            min={4}
                            max={200}
                            style={{ width: '100%' }}
                            value={config.heatmapGridResolutionY ?? undefined}
                            onChange={(val) => updateField('heatmapGridResolutionY', val)}
                            placeholder="Auto (from aspect ratio)"
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Typography.Text strong style={{ display: 'block', marginTop: 16, marginBottom: 12 }}>
                Hexagon Settings (GPS Mode)
            </Typography.Text>
            <Row gutter={12}>
                <Col span={24}>
                    <Form.Item label="Hexagon size (cm)" help="Size of each hexagon in centimeters">
                        <InputNumber
                            min={10}
                            max={500}
                            style={{ width: '100%' }}
                            value={config.hexagonSizeMeters ?? 50}
                            onChange={(val) => updateField('hexagonSizeMeters', val)}
                            placeholder="50"
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Typography.Text strong style={{ display: 'block', marginTop: 16, marginBottom: 12 }}>
                Opacity Controls
            </Typography.Text>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item label="Template image opacity (%)">
                        <InputNumber
                            min={0}
                            max={100}
                            style={{ width: '100%' }}
                            value={config.templateOpacity ?? 70}
                            onChange={(val) => updateField('templateOpacity', val)}
                            placeholder="70"
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Heatmap overlay opacity (%)">
                        <InputNumber
                            min={0}
                            max={100}
                            style={{ width: '100%' }}
                            value={config.heatmapOpacity ?? 90}
                            onChange={(val) => updateField('heatmapOpacity', val)}
                            placeholder="90"
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Typography.Text strong style={{ display: 'block', marginTop: 16, marginBottom: 12 }}>
                Color & Value Range
            </Typography.Text>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item label="Color scheme">
                        <Select
                            value={config.heatmapColorScheme ?? 'blue-red'}
                            onChange={(val) => updateField('heatmapColorScheme', val)}
                            options={[
                                { label: 'Blue → Red (cold to hot)', value: 'blue-red' },
                                { label: 'Red → Green (bad to good)', value: 'green-red' },
                                { label: 'Red → Yellow → Green (quality)', value: 'red-yellow-green' },
                                { label: 'Yellow → Red (warm)', value: 'yellow-red' },
                                { label: 'Viridis (scientific)', value: 'viridis' },
                            ]}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Invert colors">
                        <Switch
                            checked={config.invertColors ?? false}
                            onChange={(checked) => updateField('invertColors', checked)}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item label="Value range mode">
                        <Select
                            value={config.heatmapValueRangeMode ?? 'auto'}
                            onChange={(val) => updateField('heatmapValueRangeMode', val)}
                            options={[
                                { label: 'Auto (from data)', value: 'auto' },
                                { label: 'Fixed (manual)', value: 'fixed' },
                                { label: 'Area default (metric color rule)', value: 'areaDefault' },
                            ]}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={12}>
                {config.heatmapValueRangeMode === 'fixed' && (
                    <>
                        <Col span={12}>
                            <Form.Item label="Min value">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={config.heatmapValueMin ?? undefined}
                                    onChange={(val) => updateField('heatmapValueMin', val)}
                                    placeholder="e.g. 0"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Max value">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={config.heatmapValueMax ?? undefined}
                                    onChange={(val) => updateField('heatmapValueMax', val)}
                                    placeholder="e.g. 100"
                                />
                            </Form.Item>
                        </Col>
                    </>
                )}
            </Row>
        </Card>
    );
};

export default SectionConfigHeatmap;
